<script setup lang="ts">
import * as v from 'valibot'
import type { FormSubmitEvent } from '#ui/types'
import { checkUserRelations } from '~/middleware/userRelations'
import { createDirectus, rest, readMe, staticToken } from '@directus/sdk'

const schema = v.object({
  email: v.pipe(v.string(), v.email('Invalid email')),
  password: v.pipe(v.string(), v.minLength(8, 'Must be at least 8 characters'))
})

const resetSchema = v.object({
  email: v.pipe(v.string(), v.email('Invalid email')),
  confirmEmail: v.pipe(v.string(), v.email('Invalid email'))
})

type Schema = v.InferOutput<typeof schema>
type ResetSchema = v.InferOutput<typeof resetSchema>

const state = reactive({
  email: '',
  password: '',
  passwordShow: false
})

const resetState = reactive({
  email: '',
  confirmEmail: ''
})

const error = ref(false)
const showResetForm = ref(false)
const isResetting = ref(false)
const resetSuccess = ref(false)
const resetError = ref('')

const { login } = useDirectusAuth();
const email = useCookie('email')
const usersStore = useusersStore()
const { fetchUser } = useDirectusAuth();
const toast = useToast()

async function onSubmit(event: FormSubmitEvent<Schema>) {
  email.value = state.email.replaceAll(' ', '')
  try {
    await login({ email: state.email, password: state.password });

    const token = useCookie('directus_token');
    // console.log('Token récupéré:', token.value); // désactivé (sécurité : ne pas logguer le token)

    const directus = createDirectus('https://central.directus.app/')
      .with(rest())
      .with(staticToken(token.value));

    console.log('Récupération des relations complètes...');

    const userWithRelations = await directus.request(
      readMe({
        fields: [
          '*',
          'role',
          'dentsu_distributors_accesses.*',
          'dentsu_distributors_accesses.distributor.*',
          'dentsu_pos_accesses.*',
          'dentsu_pos_accesses.pos.*',
          'dentsu_distributors.*'
        ]
      })
    );

    console.log('User avec relations complètes:', userWithRelations);
    console.log('dentsu_pos_accesses détaillé:', userWithRelations.dentsu_pos_accesses);

    usersStore.user = userWithRelations;

    const relationCheck = await checkUserRelations(userWithRelations);

    if (!relationCheck.hasValidRelations && !relationCheck.isAdmin) {
      console.log(`Accès refusé pour ${userWithRelations.email}: pas de relations actives`);
      return navigateTo('/no-access');
    }

    navigateTo('/');

  } catch (e) {
    console.error('Erreur complète:', e);
    error.value = 'Invalid email or password'
  }
}

async function onResetSubmit(event: FormSubmitEvent<ResetSchema>) {
  // Vérifier que les emails correspondent
  if (resetState.email !== resetState.confirmEmail) {
    resetError.value = 'Email addresses do not match'
    return
  }

  isResetting.value = true
  resetError.value = ''
  resetSuccess.value = false

  try {
    const result = await $fetch('/api/emails/reset-password', {
      method: 'POST',
      body: {
        email: resetState.email.replaceAll(' ', '')
      }
    })

    if (result.success) {
      resetSuccess.value = true
      toast.add({
        title: 'Password reset email sent',
        description: 'If an account exists with this email, you will receive a new password shortly.',
        color: 'success'
      })

      // Réinitialiser le formulaire après 3 secondes
      setTimeout(() => {
        showResetForm.value = false
        resetState.email = ''
        resetState.confirmEmail = ''
        resetSuccess.value = false
      }, 3000)
    }
  } catch (e) {
    console.error('Error resetting password:', e)
    resetError.value = 'An error occurred. Please try again later.'
    toast.add({
      title: 'Error',
      description: 'Failed to send reset email. Please try again.',
      color: 'error'
    })
  } finally {
    isResetting.value = false
  }
}

function toggleResetForm() {
  showResetForm.value = !showResetForm.value
  resetError.value = ''
  resetSuccess.value = false
  if (!showResetForm.value) {
    resetState.email = ''
    resetState.confirmEmail = ''
  }
}

if (email) {
  state.email = email
}
</script>

<style>
button.login-button {
  --tw-ring-color: transparent;
}
</style>

<template>
  <div class="space-y-6">
    <!-- Login Form -->
    <UForm v-if="!showResetForm" :schema="v.safeParser(schema)" :state="state" class="space-y-4" @submit="onSubmit">
      <UFormField label="Email" name="email">
        <UInput v-model="state.email" class="w-full" size="xl" />
      </UFormField>

      <UFormField label="Password" name="password">
        <UInput class="w-full" size="xl" v-model="state.password" placeholder="Password"
          :type="state.passwordShow ? 'text' : 'password'" :ui="{ trailing: 'pe-1' }">
          <template #trailing>
            <UButton color="neutral" variant="link" size="sm"
              :icon="state.passwordShow ? 'i-lucide-eye-off' : 'i-lucide-eye'"
              aria-label="show ? 'Hide password' : 'Show password'" :aria-pressed="state.passwordShow"
              aria-controls="password" @click="state.passwordShow = !state.passwordShow" />
          </template>
        </UInput>
      </UFormField>

      <div v-if="error" class="text-red-500 text-sm">{{ error }}</div>

      <UButton type="submit" size="xl" variant="outline" class="
      login-button
    w-full uppercase font-semibold tracking-wide
    rounded-xl 
    bg-gradient-to-r from-gray-100 to-gray-200
    text-gray-800 
    shadow-xl transition-all duration-500
    flex items-center justify-center
    hover:from-gray-200 hover:to-gray-100 hover:shadow-md hover:-translate-y-0.5
    focus:outline-none focus:ring-0 focus:shadow-inner
    focus-visible:outline-none focus-visible:ring-0
  ">
        Login
      </UButton>

      <!-- Forgot Password Link -->
      <div class="text-center">
        <UButton variant="link" color="gray" @click="toggleResetForm" class="text-sm">
          Forgot your password?
        </UButton>
      </div>
    </UForm>

    <!-- Reset Password Form -->
    <div v-else class="space-y-4">
      <div class="mb-4">
        <h3 class="text-lg font-semibold">Reset Password</h3>
        <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Enter your email address and we'll send you a new password.
        </p>
      </div>

      <UForm :schema="v.safeParser(resetSchema)" :state="resetState" class="space-y-4" @submit="onResetSubmit">
        <UFormField label="Email" name="email">
          <UInput v-model="resetState.email" class="w-full" size="xl" placeholder="your@email.com" />
        </UFormField>

        <UFormField label="Confirm Email" name="confirmEmail">
          <UInput v-model="resetState.confirmEmail" class="w-full" size="xl" placeholder="your@email.com" />
        </UFormField>

        <div v-if="resetError" class="text-red-500 text-sm">{{ resetError }}</div>

        <div v-if="resetSuccess" class="text-green-600 text-sm bg-green-50 dark:bg-green-900/20 p-3 rounded-md">
          If your account exists, you will receive a new password shortly.
        </div>

        <div class="flex gap-3">
          <UButton type="button" size="xl" color="gray" variant="outline" @click="toggleResetForm"
            :disabled="isResetting" class="flex-1">
            Cancel
          </UButton>
          <UButton type="submit" size="xl" color="neutral" variant="outline" :loading="isResetting"
            :disabled="isResetting" class="uppercase flex-1">
            {{ isResetting ? 'Sending...' : 'Reset Password' }}
          </UButton>
        </div>
      </UForm>
    </div>
  </div>
</template>